import React, { useState, useEffect, useContext } from "react";
import { ApplicationContext } from "src/context";
import { TSkitudeAPIResponse } from "../../service/liveconditions.types";
import Link from "src/components/link";
import { getSlug } from "src/utils/slugs";
import { getLanguageField } from "src/utils/language";
import { CompactStatus, CompactWeather, CompactLifts, CompactSlopes } from "./toolbar.liveconditions.items";

type ToolbarLiveConditionsProps = {
    bars_live_conditions: {
        default_units?: string;
        local_timezone?: string;
        time_of_year?: "winter" | "summer" | "always";
        info_to_display?: {
            fields_to_show?: string[];
            resort?: {
                resort_uuid: string;
                name: string;
                zone?: string;
                status?: string;
            };
            cta_link_type?: "internal" | "external" | "page" | "none";
            cta_button_linked_page?: {
                translations: TTranslations<{ slug: string }>;
            };
            link_translations?: TTranslations<{ cta_button_link: string }>;
        };
    } | null;
    language: string;
    defaultLanguage: string;
    textColor?: string;
    hoverColor?: string;
};

type InfoToDisplay = {
    fields_to_show?: string[];
    resort?: {
        resort_uuid: string;
        name: string;
        zone?: string;
        status?: string;
    };
    cta_link_type?: "internal" | "external" | "page" | "none";
    cta_button_linked_page?: {
        translations: TTranslations<{ slug: string }>;
    };
    link_translations?: TTranslations<{ cta_button_link: string }>;
};

// Helper to get link from info_to_display
function getInfoLink(
    infoToDisplay: InfoToDisplay | undefined,
    language: string,
    defaultLanguage: string
): string | null {
    if (!infoToDisplay) return null;
    
    const linkType = infoToDisplay.cta_link_type;
    
    if (!linkType || linkType === "none") return null;
    
    if ((linkType === "internal" || linkType === "page") && infoToDisplay.cta_button_linked_page) {
        return getSlug(language, defaultLanguage, infoToDisplay.cta_button_linked_page.translations, "slug");
    }
    
    if (linkType === "external" && infoToDisplay.link_translations) {
        return getLanguageField(infoToDisplay.link_translations, language, "cta_button_link") as string | null;
    }
    
    return null;
}

export default function ToolbarLiveConditions(props: ToolbarLiveConditionsProps) {
    const [loading, setLoading] = useState(true);
    const [resortData, setResortData] = useState<TSkitudeAPIResponse | null>(null);
    
    const context = useContext(ApplicationContext);
    
    const config = props.bars_live_conditions;
    if (!config) return null;
    
    // Get resort config (single object, not array)
    const infoToDisplay = config.info_to_display;
    const resort = infoToDisplay?.resort;
    const fieldsToShow = infoToDisplay?.fields_to_show || [];
    const units = config.default_units || "metric";
    const timeOfYear = config.time_of_year || "winter";
    
    // Fetch data
    useEffect(() => {
        const fetchData = async () => {
            if (!resort?.resort_uuid) {
                setLoading(false);
                return;
            }
            
            try {
                const data = await context.fetchLiveConditions(resort.resort_uuid, resort.zone || resort.name);
                setResortData(data);
            } catch (error) {
                console.error("Error fetching toolbar live conditions:", error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchData();
    }, [resort?.resort_uuid, resort?.zone, resort?.name, context]);
    
    // If no resort configured, don't render
    if (!resort) return null;
    
    // Get link from info_to_display
    const link = getInfoLink(infoToDisplay, props.language, props.defaultLanguage);
    
    // Render field based on type
    const renderField = (field: string, index: number) => {
        switch (field) {
            case "status":
                return <CompactStatus key={`status-${index}`} isOpen={resort.status === "open"} isLoading={loading} />;
            case "weather":
                return <CompactWeather key={`weather-${index}`} resortData={resortData} units={units} isLoading={loading} />;
            case "lifts":
                return <CompactLifts key={`lifts-${index}`} resortData={resortData} timeOfYear={timeOfYear} isLoading={loading} />;
            case "slopes":
                return <CompactSlopes key={`slopes-${index}`} resortData={resortData} timeOfYear={timeOfYear} isLoading={loading} />;
            default:
                return null;
        }
    };
    
    // Filter to only supported fields for toolbar
    const supportedFields = ["status", "weather", "lifts", "slopes"];
    const fieldsToRender = fieldsToShow.filter(f => supportedFields.includes(f));
    
    if (fieldsToRender.length === 0) return null;
    
    const content = (
        <div className="flex items-center gap-3 text-[color:var(--text-color)]">
            {fieldsToRender.map((field, index) => renderField(field, index))}
        </div>
    );
    
    // Wrap in link if configured
    if (link) {
        return (
            <Link 
                href={link} 
                title="Live Conditions" 
                className="flex items-center hover:opacity-80 transition-opacity"
            >
                {content}
            </Link>
        );
    }
    
    return content;
}
